import { Box, Skeleton, Stack, Typography } from '@mui/material';
import {
    Brush,
    ComposedChart,
    Line,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import Chip from '/@/components/bayer/chip';
import colors from '/@/components/theme-provider/colors';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { HistoricalWeather } from '/@/types/weather/forecast';
import { objectEntries } from '/@/utils';
import type { UnitProviderProps } from '/@/views/visit-reports/edit/components/field-observation-section/components/precipitation-graphic';
import useReportGraphics from '../../../../hooks/report-graphics';
import CropMonitoringResultGraphic, { CustomTooltipRow } from '../common';

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
        value: number;
        payload?: {
            entries?: Array<{
                tempMin: number;
                date: string;
            }>;
        };
    }>;
    label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    const units = useUnitProvider();
    if (active && payload && payload.length) {
        const rows = [
            { label: _('Máxima'), color: '#FF3162' },
            { label: _('Media'), color: '#89D329' },
            { label: _('Mínima'), color: '#00BCFF' },
        ];
        const frostRiskDates = (payload?.[0]?.payload?.entries || [])
            .filter((entry) => units.fromTemperature(entry.tempMin, 2) <= 3)
            .map((entry) => entry.date);

        return (
            <Box
                sx={{
                    background: 'white',
                    padding: 1,
                    borderRadius: 1,
                    border: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Typography color='primary'>{label}</Typography>
                {rows.map(({ label, color }, i) => (
                    <CustomTooltipRow
                        key={label}
                        label={label}
                        value={payload[i]?.value}
                        color={color}
                        unit={units.getUnit('temperature', 'corn')}
                    />
                ))}
                {frostRiskDates.length > 0 && (
                    <Stack>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <div style={{ height: 5, width: 5, backgroundColor: '#10384F', borderRadius: '50%' }} />
                            <Typography color='primary'>{`${_('Heladas')}:`}</Typography>
                        </Stack>
                        {frostRiskDates.map((date) => (
                            <Typography key={date} pl={2} color={'primary'}>
                                {date.split('T')[0]}
                            </Typography>
                        ))}
                    </Stack>
                )}
            </Box>
        );
    }

    return null;
};

interface TemperaturesReportGraphicProps {
    cropMonitoring: CropMonitoring;
    historicalWeather: HistoricalWeather;
    unitsProp: UnitProviderProps;
    isPdf: boolean;
    width?: number;
    showOnlyGraphic?: boolean;
}

const TemperaturesReportGraphic = ({
    cropMonitoring,
    historicalWeather,
    unitsProp,
    isPdf,
    showOnlyGraphic = false,
    width,
}: TemperaturesReportGraphicProps) => {
    const { filters, filter, setFilter, data } = useReportGraphics(cropMonitoring, historicalWeather, unitsProp);
    const units = useUnitProvider(unitsProp);
    const minTempReference = units.toTemperature(3, 2).value;
    const maxTempReference = units.toTemperature(35, 2).value;
    const crop = cropMonitoring?.crop;

    const renderChart = () => {
        const chartContent = (
            <>
                <XAxis
                    dataKey='date'
                    tickLine={false}
                    axisLine={false}
                    interval='preserveStartEnd'
                    style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: colors.gray.default,
                    }}
                />
                <YAxis
                    tickLine={false}
                    axisLine={false}
                    interval='preserveStartEnd'
                    type='number'
                    style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: colors.gray.default,
                    }}
                    tickFormatter={(value) => `${value} ${units.getUnit('temperature', 'corn')}`}
                    domain={['auto', 'auto']}
                />
                {!isPdf && <Tooltip content={<CustomTooltip />} />}
                <Line dataKey='tempMax' fill='#FF3162' stroke='#FF3162' type='monotone' />
                <Line dataKey='tempAvg' fill='#89D329' stroke='#89D329' type='monotone' />
                <Line dataKey='tempMin' fill='#00BCFF' stroke='#00BCFF' type='monotone' />

                <ReferenceLine
                    y={maxTempReference}
                    label={{
                        position: 'right',
                        value: `${maxTempReference}`,
                        fill: 'red',
                        style: { fontSize: 12, fontWeight: 600 },
                    }}
                    stroke='red'
                    strokeDasharray='3 3'
                />
                <ReferenceArea y1={maxTempReference} fill='#FFE6E6' />

                <ReferenceLine
                    y={minTempReference}
                    label={{
                        position: 'right',
                        value: `${minTempReference}`,
                        fill: 'cornflowerblue',
                        style: { fontSize: 12, fontWeight: 600 },
                    }}
                    stroke='cornflowerblue'
                    strokeDasharray='3 3'
                />
                <ReferenceArea y2={minTempReference} fill='#F0F6FF' />

                {!isPdf && data.length > 12 && (
                    <Brush
                        dataKey='date'
                        height={30}
                        stroke={colors.themes[settings.app.theme].secondary.default}
                        endIndex={11}
                        tickFormatter={() => ''}
                    />
                )}
            </>
        );

        return isPdf ? (
            <Box
                sx={{
                    '& svg': {
                        overflow: 'visible',
                    },
                }}
            >
                <ComposedChart
                    width={width}
                    height={400}
                    data={data}
                    margin={{ top: 0, right: 0, left: -10, bottom: 0 }}
                >
                    {chartContent}
                </ComposedChart>
            </Box>
        ) : (
            <ResponsiveContainer height={400}>
                <ComposedChart data={data} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
                    {chartContent}
                </ComposedChart>
            </ResponsiveContainer>
        );
    };

    if (showOnlyGraphic && historicalWeather) {
        return <Box sx={{ width: '100%', height: '400px', px: 1 }}>{renderChart()}</Box>;
    }

    return (
        <CropMonitoringResultGraphic
            title={_('Temperaturas y heladas')}
            description={_('Analizá los rangos térmicos por etapa del cultivo para entender su desarrollo.')}
            references={[
                { label: _('Máxima'), color: '#FF3162' },
                { label: _('Media'), color: '#89D329' },
                { label: _('Mínima'), color: '#00BCFF' },
                { label: _('Riesgo de stress térmico'), color: 'red', dashed: true },
                { label: _('Riesgo de helada'), color: 'cornflowerblue', dashed: true },
            ]}
        >
            {!isPdf && crop === 'corn' && (
                <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                    {objectEntries(filters).map(([key, { name }]) => (
                        <Chip key={key} label={name} onClick={() => setFilter(key)} selected={filter === key} />
                    ))}
                </Stack>
            )}
            {!historicalWeather ? (
                <Skeleton variant='rectangular' height='400px' />
            ) : (
                <div style={{ width: '100%', height: '400px' }}>{renderChart()}</div>
            )}
        </CropMonitoringResultGraphic>
    );
};

export default TemperaturesReportGraphic;
